/**
 * Page 3 — Scorecard.
 *
 * Five things: the legend, summary tiles, a department filter, every score with its
 * working shown — value / peer average / points earned, per rule — and, since
 * 2026-09-22, every employee month by month.
 *
 * Showing the peer average next to the value is the point of this page. A score is
 * meaningless without the bar it was measured against, and the bar moves per person.
 *
 * The month-by-month grid used to be its own tab. It is here because the question it
 * answers — "did this hold up all year, or is the total carried by one month" — is a
 * question about the score, and the two were a navigation apart.
 *
 * The rules are NOT hardcoded into columns any more. They are read off SCORE_RULES,
 * so adding the next one Shayna approves is a change in one place rather than a
 * column, a legend row, a peer-average helper and a summary tile that can each be
 * forgotten separately.
 */
import { useMemo, useState } from 'react'
import { useAppData } from '@/data/AppData'
import { scoreStanding, shoreVendor, type Person } from '@/data/selectors'
import { rankShort, rankText } from '@/measures/rankText'
import {
  SCORE_RULES,
  PENALTY,
  SCORE_MAX,
  formatPoints,
  peerAverage,
  type ScoreKey,
} from '@/measures/scorecard'
import { monthScorer, MONTH_MAX, MONTH_ONLY_GAPS } from '@/measures/monthScore'
import {
  EmployeeMonthGrid,
  exportButton,
  MONTH_METRICS,
  SCORE_KEY_OF,
  type GridMode,
} from '@/components/EmployeeMonthGrid'
import { DataTable, type Column } from '@/components/Table'
import { PopOut } from '@/components/PopOut'
import { TrailingBadge, useTrailingCaption } from '@/components/TrailingScore'
import { StatStrip, Stat, ScoreBadge, Select, PersonCell } from '@/components/Bits'
import { ScoreTip } from '@/components/ScoreTip'
import { money2, int, pct, dec, monthSpan } from '@/format'
import { ALL } from '@/data/filters'
import { copyRows, downloadCSV, downloadXLSXBook, type ExportColumn } from '@/ui/export'
import { allOptionSheets, allOptionsTable } from '@/components/monthGridExport'
import { define, withNote } from '@/measures/definitions'
import { useUrlState, text, maybeText, bool } from '@/ui/urlState'

/** How each rule's value and its peer average are printed. */
const FORMAT: Record<ScoreKey, (v: number | null) => string> = {
  costPerClaim: money2,
  claims: (v) => int(v),
  collectedPct: (v) => pct(v),
  closedClaimPct: (v) => pct(v),
  zeroTouchShare: (v) => pct(v),
  openWorkedPct: (v) => pct(v),
  facilities: (v) => dec(v, 1),
}

const VALUE_OF: Record<ScoreKey, (p: Person) => number | null> = {
  costPerClaim: (p) => p.costPerClaim,
  claims: (p) => p.claims,
  collectedPct: (p) => p.collectedPct,
  closedClaimPct: (p) => p.closedClaimPct,
  zeroTouchShare: (p) => p.zeroTouchShare,
  openWorkedPct: (p) => p.openWorkedPct,
  facilities: (p) => p.facilities,
}

/** One rule's working: the value, the bar it was measured against, and what it earned. */
function RuleCell({
  value,
  average,
  points,
  earned,
  missing,
  format,
}: {
  value: number | null
  average: number | null
  points: number
  earned: boolean
  missing: boolean
  format: (v: number | null) => string
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1,
        background: earned ? 'var(--good-bg)' : 'transparent',
        borderRadius: 4,
        padding: '3px 6px',
        margin: '-3px -6px',
      }}
      title={
        missing
          ? 'No figure for this person, so the line scores nothing. It is not a zero.'
          : undefined
      }
    >
      <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
        {format(value)}
      </span>
      <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
        avg {format(average)}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 'var(--t-micro)',
          color: earned ? 'var(--good-ink)' : 'var(--ink-3)',
          fontWeight: earned ? 600 : 400,
          // A missing line is marked, not faded. Fading it would put it under AA and
          // would also hide it from a contrast audit, since opacity lives on the
          // ancestor and never reaches the child's computed style.
          fontStyle: missing ? 'italic' : 'normal',
        }}
      >
        {missing ? 'no figure' : earned ? formatPoints(points) : '0'}
      </span>
    </div>
  )
}

/** The Ledger's four bands: good from 65% of the maximum, mid from 30%, low from 0. */
function scoreClass(t: number): string {
  return t >= SCORE_MAX * 0.65 ? 'good' : t >= SCORE_MAX * 0.3 ? 'mid' : t >= 0 ? 'low' : 'bad'
}

export function Scorecard() {
  const { derived, scope, openPerson, openMonths, trailing } = useAppData()
  const trail = useTrailingCaption()
  const trailingTotal = (p: Person) => {
    const t = trailing.byName.get(p.employee)
    return t?.scored ? t.total : null
  }
  /** The months every rank on the page covers, for its hover. */
  const period = monthSpan(derived.months)
  const [dept, setDept] = useUrlState('dept', ALL, text)

  // The scorecard's peers: active staff only.
  const allStats = derived.peerStats

  /** Everyone scored in the department — what the tiles and the score chips count. */
  const scoredRows = useMemo(
    () =>
      scope.people.filter(
        (p) => p.score.scored && (dept === ALL || p.department === dept),
      ),
    [scope.people, dept],
  )
  /**
   * A score chip picked under the tiles: the table, the export and the month grid then
   * show only the people with that total (Shayna's review #18, 2026-10-02). A pick no
   * chip shows any more — after a department change, say — is ignored, not an empty page.
   */
  const [scoreParam, setScoreParam] = useUrlState('score', null, maybeText)
  const pickedScore = useMemo(() => {
    const v = scoreParam === null ? NaN : Number(scoreParam)
    return Number.isFinite(v) && scoredRows.some((p) => p.score.total === v) ? v : null
  }, [scoreParam, scoredRows])
  const rows = useMemo(
    () => (pickedScore === null ? scoredRows : scoredRows.filter((p) => p.score.total === pickedScore)),
    [scoredRows, pickedScore],
  )
  const pickScore = (t: number | null) => setScoreParam(t === null || t === pickedScore ? null : String(t))
  /**
   * People in scope with claims who are left off because they have left: nobody who
   * has left is scored (DECISION-LOG.md §8). Said on the page, since the Employee
   * status control would otherwise seem to have done nothing here.
   */
  const unscoredTerminated = useMemo(
    () =>
      scope.people.filter(
        (p) => p.score.terminated && p.claims > 0 && (dept === ALL || p.department === dept),
      ).length,
    [scope.people, dept],
  )

  const [saveNote, setSaveNote] = useState<string | null>(null)

  /*
   * The export carries the value, the bar it was measured against and the points —
   * the three things the cell shows. A CSV of values alone cannot be checked against
   * the score, which is the reason somebody exports this table in the first place.
   */
  const exportColumns: ExportColumn<Person>[] = [
    { header: 'Employee', value: (p) => p.employee },
    { header: 'Paylocity ID', value: (p) => p.paylocityId ?? '' },
    { header: 'Job title', value: (p) => p.jobTitle },
    { header: 'Department', value: (p) => p.department },
    { header: 'Shore', value: (p) => p.shore },
    { header: `Total score (of ${SCORE_MAX}), ${period}`, value: (p) => p.score.total },
    { header: `${trail.label} score, ${trail.span}`, value: trailingTotal },
    ...SCORE_RULES.flatMap((rule): ExportColumn<Person>[] => [
      { header: rule.label, value: (p) => VALUE_OF[rule.key](p) },
      {
        header: `${rule.label} — peer average`,
        value: (p) => p.score.lines.find((l) => l.key === rule.key)?.peerAvg ?? null,
      },
      {
        header: `${rule.label} — points (of ${rule.pts})`,
        value: (p) => p.score.lines.find((l) => l.key === rule.key)?.earned ?? 0,
      },
    ]),
    { header: 'Penalty', value: (p) => (p.score.penalty.applied ? PENALTY.pts : 0) },
    { header: 'Peers', value: (p) => p.score.peerN },
  ]

  const copyScores = async () => {
    const r = await copyRows(rows, exportColumns)
    setSaveNote(r === 'ok' ? null : 'Clipboard refused — use CSV instead.')
  }
  const saveScores = () => {
    const r = downloadCSV('employee-scorecard.csv', rows, exportColumns)
    setSaveNote(
      r === 'ok' ? null : 'This view cannot start a download — an embedded preview blocks it.',
    )
  }

  /*
   * The ceiling anyone can actually reach on THIS bundle.
   *
   * SCORE_MAX is 11, but collection rate is null on every person in the curated
   * model, so its 2 points are unearnable and the true ceiling is 9.5. A tile reading
   * "At +11: 0" is not a finding about performance — it is a fact about the
   * pipeline, and printed as a score it looks like nobody is doing well.
   */
  const attainable = useMemo(
    () =>
      SCORE_RULES.filter((r) =>
        derived.people.some((p) => {
          const v = VALUE_OF[r.key](p)
          return v !== null && Number.isFinite(v)
        }),
      ).reduce((s, r) => s + r.pts, 0),
    [derived.people],
  )

  const summary = useMemo(() => {
    const ts = scoredRows.map((p) => p.score.total)
    const dist = new Map<number, number>()
    for (const t of ts) dist.set(t, (dist.get(t) ?? 0) + 1)
    return {
      scored: scoredRows.length,
      average: ts.length ? ts.reduce((a, b) => a + b, 0) / ts.length : null,
      atMax: ts.filter((t) => t === attainable).length,
      penalised: scoredRows.filter((p) => p.score.penalty.applied).length,
      dist,
    }
  }, [scoredRows, attainable])

  const columns: Column<Person>[] = [
    {
      key: 'person',
      header: 'Employee',
      frozen: true,
      // Wider, and clamped. "Medicaid Collections Supervisor · Medicaid - Signet ·
      // On-shore" wrapped to three lines while "Roth, Ruchy · HMO" took one, so the
      // table had rows of two different heights and nothing lined up down a column.
      // The full string stays available on hover.
      width: 268,
      sort: (p) => p.employee,
      render: (p) => (
        <PersonCell
          name={p.employee}
          meta={`${p.jobTitle} · ${p.department}${shoreVendor(p)}`}
        />
      ),
    },
    {
      // Review #373: the key other systems know the person by.
      key: 'paylocityId',
      header: 'Paylocity ID',
      dim: true,
      width: 96,
      sort: (p) => p.paylocityId,
      render: (p) => (
        <span className="mono" style={{ fontSize: 'var(--t-rank)' }}>
          {p.paylocityId ?? '—'}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total score',
      help: define('score'),
      align: 'right',
      sort: (p) => p.score.total,
      render: (p) => (
        <div style={{ display: 'grid', gap: 2, justifyItems: 'end' }}>
          <ScoreTip score={p.score} who={`${p.department} · ${p.jobTitle}`}>
            <ScoreBadge total={p.score.total} scored={p.score.scored} lowVolume={p.score.lowVolume} />
          </ScoreTip>
          {p.score.scored && (
            <span
              className="mono"
              style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}
            >
              of {formatPoints(SCORE_MAX).replace('+', '')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'trailing',
      header: trail.label,
      title: trail.note,
      align: 'right',
      sort: trailingTotal,
      render: (p) => (
        <div style={{ display: 'grid', gap: 2, justifyItems: 'end' }}>
          <TrailingBadge
            score={trailing.byName.get(p.employee)}
            who={`${p.department} · ${p.jobTitle}`}
          />
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
            {trail.span}
          </span>
        </div>
      ),
    },
    {
      key: 'rank',
      header: 'Rank',
      help: define('rank'),
      align: 'right',
      dim: true,
      // Rank by score WITHIN the person's own job title — the pool every rank in the
      // app uses (MEASURE-RULES §10.7) — not within the table. A table rank would
      // change every time somebody typed in the filter bar.
      sort: (p) => scoreStanding(scope.cohorts, p, trailing.byName).rank,
      render: (p) => {
        const r = scoreStanding(scope.cohorts, p, trailing.byName)
        return (
          <span
            className="mono"
            style={{ fontSize: 'var(--t-table)' }}
            title={rankText(r, period, 'score')}
          >
            {r.rank === null ? (
              <span style={{ color: 'var(--ink-3)' }}>{rankShort(r)}</span>
            ) : (
              `${r.rank} of ${r.of}`
            )}
          </span>
        )
      },
    },
    ...SCORE_RULES.map(
      (rule): Column<Person> => ({
        key: rule.key,
        // The points are part of the column's name. A reader comparing two cells
        // needs to know one is worth 3 and the other 0.5 without crossing to the
        // legend, which on a wide table is off screen.
        header: `${rule.label} ${formatPoints(rule.pts)}`,
        help: withNote(
          define(rule.key),
          `Worth ${formatPoints(rule.pts)} when the person beats their peer average.`,
        ),
        align: 'right',
        band: true,
        sort: (p) => VALUE_OF[rule.key](p),
        render: (p) => {
          const line = p.score.lines.find((l) => l.key === rule.key)
          return (
            <RuleCell
              value={line?.value ?? null}
              average={line?.peerAvg ?? peerAverage(allStats, p.stats, rule)}
              points={rule.pts}
              earned={(line?.earned ?? 0) > 0}
              missing={line?.missing ?? true}
              format={FORMAT[rule.key]}
            />
          )
        },
      }),
    ),
    {
      key: 'penalty',
      header: 'Penalty',
      help: define('penalty'),
      align: 'right',
      divider: true,
      sort: (p) => (p.score.penalty.applied ? PENALTY.pts : 0),
      render: (p) =>
        p.score.penalty.applied ? (
          <span
            className="mono"
            style={{ fontSize: 'var(--t-table)', color: 'var(--crit-ink)', fontWeight: 600 }}
            title={`${PENALTY.label} — absolute, not vs peers`}
          >
            −3
          </span>
        ) : (
          <span className="mono" style={{ fontSize: 'var(--t-table)', color: 'var(--ink-3)' }}>
            —
          </span>
        ),
    },
    {
      key: 'cohort',
      header: 'Peers',
      help: define('peers'),
      align: 'right',
      dim: true,
      sort: (p) => p.score.peerN,
      render: (p) => (
        <span
          className="mono"
          style={{ fontSize: 'var(--t-table)' }}
          title={`Cost per claim compares against ${p.score.shoreN} same-shore peers`}
        >
          {p.score.peerN}
        </span>
      ),
    },
  ]

  const scoreTable = (
    <DataTable
      storageKey="scorecard.people"
      rows={rows}
      columns={columns}
      rowKey={(p) => p.employee}
      onRowClick={(p) => openPerson(p.employee)}
      sortParam="sort"
      initialSort="total"
      initialDir="desc"
      maxHeight="62vh"
      empty="Nobody in this department carries attributed claims."
    />
  )

  return (
    <>
      <h1 className="page-title">Employee scorecard</h1>
      {/* Which months every score on the page is for, said once at the top. */}
      <p className="body-note" style={{ margin: '4px 0 0' }} data-testid="score-range">
        Scores for <b>{period}</b>, each person’s figures pooled over the range.{' '}
        <span title={trail.note}>
          {trail.label} score beside it: <b>{trail.span}</b>.
        </span>
      </p>
      {/*
        The month-by-month grid sits under a long table, and nobody found it (#24,
        2026-10-02). The table now scrolls inside its own box, and this says the grid
        is there.
      */}
      <p className="body-note" style={{ margin: '6px 0 0' }}>
        <button type="button" className="jump-link" onClick={() => jumpTo('every-score')}>
          Every score ↓
        </button>
        <span style={{ color: 'var(--ink-3)' }}> · </span>
        <button type="button" className="jump-link" onClick={() => jumpTo('month-by-month')}>
          Every employee, month by month ↓
        </button>
      </p>

      <section
        style={{
          marginTop: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 330px), 1fr))',
          gap: 16,
        }}
      >
        <div className="card">
          <h3 className="card-heading">How a score is built</h3>
          <table className="sclegend">
            <tbody>
              {SCORE_RULES.map((r) => (
                <tr key={r.key}>
                  <td>{r.label}</td>
                  <td>{r.lowerIsBetter ? 'Below' : 'Above'} peer-group average</td>
                  <td className="p good">{formatPoints(r.pts)}</td>
                </tr>
              ))}
              <tr>
                <td>{PENALTY.label}</td>
                <td>Absolute, not against peers</td>
                <td className="p bad">−3</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div>
          <StatStrip cols={4}>
            {/* Every tile says what its number is OF. "Average score 5.1" on its own
                invites the reader to supply a denominator, and the one they supply is
                usually 10. */}
            <Stat
              label="Employees scored"
              value={int(summary.scored)}
              note={`${dept === ALL ? 'all departments' : dept}${
                unscoredTerminated ? ` · ${int(unscoredTerminated)} terminated, not scored` : ''
              }`}
            />
            <Stat
              label="Average score"
              value={dec(summary.average, 1)}
              note={`of a possible ${formatPoints(SCORE_MAX).replace('+', '')}`}
            />
            <Stat
              label={`Scoring ${formatPoints(attainable).replace('+', '')}`}
              value={int(summary.atMax)}
              note={
                attainable === SCORE_MAX
                  ? 'beat every peer average'
                  : `the ceiling on this bundle, not ${formatPoints(SCORE_MAX)}`
              }
            />
            <Stat
              label="Carrying the penalty"
              value={int(summary.penalised)}
              note="under 50% billed vs resolved"
            />
          </StatStrip>

          {/* Each chip is a filter: click to see only the people with that score. */}
          <div className="scdist" role="group" aria-label="Filter by total score">
            {[...summary.dist.entries()]
              .sort((a, b) => b[0] - a[0])
              .map(([t, n]) => (
                <button
                  type="button"
                  key={t}
                  className={`scchip ${scoreClass(t)}${pickedScore === t ? ' is-picked' : ''}`}
                  aria-pressed={pickedScore === t}
                  title={
                    pickedScore === t
                      ? 'Show everyone again'
                      : `Show only the ${n === 1 ? 'person' : `${n} people`} scoring ${formatPoints(t)}`
                  }
                  onClick={() => pickScore(t)}
                >
                  {formatPoints(t)}
                  <b>{n}</b>
                </button>
              ))}
          </div>
          {pickedScore !== null && (
            <p className="body-note" style={{ margin: '8px 0 0', fontSize: 'var(--t-micro)' }}>
              Showing the {int(rows.length)} {rows.length === 1 ? 'person' : 'people'} scoring{' '}
              <b>{formatPoints(pickedScore)}</b> below.{' '}
              <button type="button" className="jump-link" onClick={() => pickScore(null)}>
                Show everyone
              </button>
            </p>
          )}

          <div style={{ marginTop: 16, maxWidth: 260 }}>
            <Select
              label="Department"
              value={dept}
              options={[
                { value: ALL, label: 'All departments' },
                ...derived.departments.map((d) => ({ value: d, label: d })),
              ]}
              onChange={setDept}
            />
          </div>
        </div>
      </section>

      {/* The score table in the full window — Shayna, 2026-10-02 (#19). The shared
          pop-out lifts this same section, so the sort and the picked score carry over. */}
      <PopOut
        title={pickedScore === null ? 'Every score' : `Scoring ${formatPoints(pickedScore)}`}
        note={`${int(rows.length)} employees scored · ${dept === ALL ? 'all departments' : dept}`}
      >
        {({ button, enlarged }) => (
          <section className="section popout-fill" id="every-score">
            {!enlarged && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <h2 className="section-heading" style={{ margin: 0 }}>
                  {pickedScore === null ? 'Every score' : `Scoring ${formatPoints(pickedScore)}`}
                </h2>
                {pickedScore !== null && (
                  <span className="pill-sel">
                    {int(rows.length)} of {int(scoredRows.length)}
                    <button type="button" aria-label="Show everyone" onClick={() => pickScore(null)}>
                      ×
                    </button>
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {button}
                  <span className="eyebrow" style={{ marginLeft: 6 }}>Export</span>
                  <button type="button" onClick={() => void copyScores()} style={miniButton}>
                    Copy
                  </button>
                  <button type="button" onClick={() => saveScores()} style={miniButton}>
                    CSV
                  </button>
                </div>
              </div>
            )}
            {enlarged && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                {pickedScore !== null && (
                  <span className="pill-sel" style={{ marginRight: 'auto' }}>
                    {int(rows.length)} of {int(scoredRows.length)}
                    <button type="button" aria-label="Show everyone" onClick={() => pickScore(null)}>
                      ×
                    </button>
                  </span>
                )}
                <span className="eyebrow">Export</span>
                <button type="button" onClick={() => void copyScores()} style={miniButton}>
                  Copy
                </button>
                <button type="button" onClick={() => saveScores()} style={miniButton}>
                  CSV
                </button>
              </div>
            )}
            {saveNote && (
              <p
                className="mono"
                style={{ fontSize: 'var(--t-micro)', color: 'var(--crit-ink)', marginTop: 8 }}
              >
                {saveNote}
              </p>
            )}
            {/* Its own scroll box with a sticky header, so the page under it stays reachable. */}
            <div className="card sc-table" style={{ marginTop: 12, padding: 0, overflow: 'hidden' }}>
              {scoreTable}
            </div>
            {!enlarged && (
              <p
                className="mono"
                style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)', marginTop: 10 }}
              >
                {pickedScore === null
                  ? `${int(rows.length)} employees scored`
                  : `${int(rows.length)} of ${int(scoredRows.length)} employees scored — those scoring ${formatPoints(pickedScore)}`}
              </p>
            )}
          </section>
        )}
      </PopOut>

      <MonthByMonth
        people={rows}
        allPeople={derived.people}
        months={scope.months}
        openMonths={openMonths}
        onOpenPerson={openPerson}
      />
    </>
  )
}

/** Scroll a section of this page into view without touching the URL's page state. */
function jumpTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/* --------------------------------------------- every employee, month by month */

/** Shared by the two small export buttons on this page. */
const miniButton: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '3px 10px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  cursor: 'pointer',
}

const gridSelect: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 7px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  width: '100%',
}

const SCORED_METRICS = MONTH_METRICS.filter((m) => m.group === 'scorecard')
const OTHER_METRICS = MONTH_METRICS.filter((m) => m.group === 'other')

/** The default view: month score out of MONTH_MAX (11), −3 penalty included since 2026-10-21. */
const SCORE_OPTION = '__score__'

function MonthByMonth({
  people,
  allPeople,
  months,
  openMonths,
  onOpenPerson,
}: {
  people: Person[]
  allPeople: Person[]
  months: string[]
  openMonths: string[]
  onOpenPerson: (name: string) => void
}) {
  // In the URL, so Back and a copied link keep the grid's measure and cells.
  const [choice, setChoice] = useUrlState('grid', SCORE_OPTION, text)
  const [asPoints, setAsPoints] = useUrlState('cells', false, bool)

  // Peer averages come from the UNFILTERED population, so narrowing the department
  // filter cannot quietly re-cut who each person is being compared against.
  const scorer = useMemo(() => monthScorer(allPeople, months), [allPeople, months])
  // The build's claim-volume warning, marked ⚑ (review #374, measures/claimVolume.ts).
  const { volume } = useAppData()

  const metric =
    choice === SCORE_OPTION
      ? SCORED_METRICS[0]!
      : (MONTH_METRICS.find((m) => m.key === choice) ?? SCORED_METRICS[0]!)

  const scored = choice !== SCORE_OPTION && SCORE_KEY_OF[choice] !== undefined
  const mode: GridMode =
    choice === SCORE_OPTION ? 'score' : asPoints && scored ? 'points' : 'value'

  /*
   * Every "Cells show" option in one file (review #178): Max picked each, exported it
   * and picked the next. Excel gets a sheet per option; CSV a column group per option.
   */
  const [allNote, setAllNote] = useState<string | null>(null)
  const saveAll = (kind: 'xlsx' | 'csv') => {
    const result =
      kind === 'xlsx'
        ? downloadXLSXBook(
            'month-by-month-all-options.xlsx',
            allOptionSheets(scorer, people, months, openMonths),
          )
        : (() => {
            const t = allOptionsTable(scorer, people, months, openMonths)
            return downloadCSV('month-by-month-all-options.csv', t.rows, t.columns)
          })()
    setAllNote(result === 'ok' ? null : 'This view cannot start a download.')
  }

  return (
    <PopOut title="Every employee, month by month">
      {({ button, enlarged }) => (
        <section className="section popout-fill" id="month-by-month">
          {!enlarged && <h2 className="section-heading">Every employee, month by month</h2>}

          <div
            style={{
              display: 'flex',
              gap: 14,
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              marginTop: 12,
              marginBottom: 14,
            }}
          >
            <label style={{ display: 'grid', gap: 4, minWidth: 250 }}>
              <span className="eyebrow">Cells show</span>
              <select
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                style={gridSelect}
              >
                <optgroup label="Scorecard">
                  <option value={SCORE_OPTION}>Month score (of {MONTH_MAX})</option>
                  {SCORED_METRICS.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Other measures">
                  {OTHER_METRICS.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>

            {/*
              A second select rather than a checkbox, per the artifact — "as: scorecard
              points" reads as a choice about the cells, where a tick labelled "show
              points instead" reads as a setting. It only appears on a metric that
              carries points; a disabled control would be a worse answer than none.
            */}
            <label style={{ display: 'grid', gap: 4, minWidth: 180 }}>
              <span className="eyebrow">As</span>
              {scored ? (
                <select
                  value={asPoints ? 'points' : 'value'}
                  onChange={(e) => setAsPoints(e.target.value === 'points')}
                  style={gridSelect}
                >
                  <option value="value">The measure</option>
                  <option value="points">Scorecard points</option>
                </select>
              ) : (
                <span
                  style={{
                    fontSize: 'var(--t-table)',
                    color: 'var(--ink-3)',
                    padding: '5px 7px',
                    border: '1px dashed var(--rule-2)',
                    borderRadius: 6,
                    background: 'var(--surface-2)',
                  }}
                >
                  {choice === SCORE_OPTION ? 'Scorecard points' : 'Not a scored measure'}
                </span>
              )}
            </label>
            <span style={{ marginLeft: 'auto' }}>{button}</span>
          </div>

          <p className="body-note" style={{ marginTop: 0 }}>
            {MONTH_ONLY_GAPS.length === 0 ? (
              <>
                Each month is scored out of <strong>{MONTH_MAX}</strong>, same as the period.
              </>
            ) : (
              <>
                A month is scored out of <strong>{MONTH_MAX}</strong>, not {SCORE_MAX}:{' '}
                {MONTH_ONLY_GAPS.join(' and ')}{' '}
                {MONTH_ONLY_GAPS.length === 1 ? 'has' : 'have'} no monthly figure in this
                bundle, so {MONTH_ONLY_GAPS.length === 1 ? 'it is' : 'they are'} left out
                of the monthly total rather than scored as a miss.
              </>
            )}
          </p>

          <EmployeeMonthGrid
            metric={metric}
            mode={mode}
            scorer={scorer}
            people={people}
            months={months}
            openMonths={openMonths}
            onOpenPerson={onOpenPerson}
            volume={volume}
            exportMore={
              <span
                style={{ display: 'inline-flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}
                data-testid="export-all-options"
              >
                <span className="eyebrow">Every “Cells show” option, one file</span>
                <button
                  type="button"
                  style={exportButton}
                  title="One sheet per option, each as that option exports on its own"
                  onClick={() => saveAll('xlsx')}
                >
                  Excel
                </button>
                <button
                  type="button"
                  style={exportButton}
                  title="One row per person, a column group per option"
                  onClick={() => saveAll('csv')}
                >
                  CSV
                </button>
                {allNote && (
                  <em style={{ fontSize: 'var(--t-rank)', color: 'var(--crit-ink)' }}>{allNote}</em>
                )}
              </span>
            }
          />
        </section>
      )}
    </PopOut>
  )
}
